import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_NOTIZEN } from "../makros";

export const lektionSprechernotizen: Lesson = {
  id: "sprechernotizen",
  title: "Sprechernotizen",
  summary: "Der Ort für alles, was gesagt, aber nicht gezeigt wird.",
  durationMinutes: 8,
  learningGoals: [
    "Sprechernotizen als Gegenstück zur schlanken Folie einsetzen",
    "Notizen so schreiben, dass sie im Vortrag wirklich helfen",
    "Notizenseiten drucken und weitergeben",
  ],
  steps: [
    {
      id: "notizenbereich",
      title: "Der Notizenbereich",
      explanation: [
        "Unter jeder Folie liegt ein Notizenbereich. Sichtbar wird er über " +
          "„Ansicht“ → „Notizen“ oder über die Schaltfläche „Notizen“ in der " +
          "Statusleiste am unteren Rand. Zieht man den Trennstrich nach oben, wird der " +
          "Bereich größer.",
        "Was dort steht, sieht nur die vortragende Person – in der Referentenansicht " +
          "neben der aktuellen Folie. Auf dem Beamer erscheint es nicht.",
        "Damit lösen die Sprechernotizen den Zielkonflikt aus dem Modul „Text auf " +
          "Folien“: Die Folie bleibt schlank, weil alles, was gesagt und nicht gezeigt " +
          "werden soll, im Notizenbereich landet. Beim Kürzen einer überladenen Folie ist " +
          "das der richtige Reflex – nicht löschen, sondern nach unten verschieben.",
      ],
      keyPoints: [
        "Ansicht → Notizen oder die Schaltfläche „Notizen“ in der Statusleiste.",
        "Notizen sind nur in der Referentenansicht sichtbar, nie auf dem Beamer.",
        "Gekürzter Folientext wandert in die Notizen, statt verloren zu gehen.",
      ],
    },
    {
      id: "was-hineingehoert",
      title: "Was in die Notizen gehört",
      explanation: [
        "Ein häufiger Fehler ist, den Vortrag Wort für Wort in die Notizen zu schreiben. " +
          "Das fühlt sich sicher an und führt beim Vortragen dazu, dass man abliest – " +
          "hörbar, weil geschriebene Sprache anders klingt als gesprochene.",
        "Brauchbare Notizen bestehen aus vier Dingen: den Zahlen, die man sich nicht " +
          "merken kann, aber genau nennen muss. Den zwei bis drei Stichworten, die die " +
          "Reihenfolge der Argumente festhalten. Dem Übergangssatz zur nächsten Folie – " +
          "genau dort stockt ein Vortrag am häufigsten. Und den Antworten auf die Fragen, " +
          "die erfahrungsgemäß kommen.",
        "Zusätzlich lohnt sich eine Zeitmarke: „hier ca. Minute 8“. In der " +
          "Referentenansicht läuft eine Uhr mit – ein Blick genügt dann, um zu wissen, ob " +
          "man vor oder hinter dem Plan liegt.",
        "Sprechernotizen sind übrigens auch der richtige Ort für organisatorische " +
          "Hinweise an sich selbst: „Licht dimmen“, „Video hat Ton“, „Handout erst danach " +
          "verteilen“.",
      ],
      keyPoints: [
        "Stichworte statt ausformulierter Sätze – sonst liest man ab.",
        "Zahlen, Argumentreihenfolge, Übergangssatz, erwartete Fragen.",
        "Zeitmarken helfen zusammen mit der Uhr der Referentenansicht.",
      ],
    },
    {
      id: "notizenseiten",
      title: "Notizenseiten drucken und weitergeben",
      explanation: [
        "Über „Ansicht“ → „Notizenseite“ zeigt PowerPoint jede Folie als eigene Seite " +
          "mit der Folie oben und den Notizen darunter. Dieses Format lässt sich drucken " +
          "und als PDF speichern: „Datei“ → „Drucken“ → unter „Ganzseitige Folien“ auf " +
          "„Notizenseiten“ umstellen.",
        "Damit ist die Frage aus dem Modul „Text auf Folien“ endgültig beantwortet. Wer " +
          "eine Präsentation zum Nachlesen weitergeben will, verschickt nicht die Folien, " +
          "sondern die Notizenseiten als PDF. Die Folien bleiben schlank, die Nachlese " +
          "ist vollständig, und beide Formate sind für ihren Zweck gemacht.",
        "Das Aussehen der Notizenseiten lässt sich unter „Ansicht“ → „Notizenmaster“ " +
          "anpassen – dieselbe Idee wie beim Folienmaster, nur für die Druckfassung.",
        "Ein Hinweis für den Fall, dass es schnell gehen muss: Notizen werden beim " +
          "Speichern als PDF standardmäßig nicht mitgenommen. Wer sie braucht, muss im " +
          "Druckdialog ausdrücklich „Notizenseiten“ wählen.",
      ],
      keyPoints: [
        "Ansicht → Notizenseite zeigt das Druckformat.",
        "Datei → Drucken → „Notizenseiten“ statt „Ganzseitige Folien“.",
        "Ansicht → Notizenmaster gestaltet die Notizenseiten.",
      ],
    },
    {
      id: "notizen-schreiben",
      title: "Notizen schreiben – und wie der Kurs sie prüft",
      explanation: [
        "Jetzt geht es ans Schreiben: Nimm dir zwei oder drei deiner Folien vor und " +
          "schreib Notizen nach dem Muster oben – Zahlen, Stichworte zur Reihenfolge, den " +
          "Übergangssatz zur nächsten Folie.",
        "Die Office.js-Schnittstelle, über die dieser Kurs sonst prüft, kennt den " +
          "Notizenbereich nicht. Deshalb liest hier ein Prüfmakro die Notizen über das " +
          "VBA-Objektmodell aus und legt das Ergebnis in der Datei ab. Das Makro liest nur – " +
          "an der Präsentation ändert es nichts.",
        "Geprüft wird die Menge: Notizen sollen da sein, aber kein Manuskript. Mehr als " +
          "etwa 120 Wörter auf einer Folie sind ein Zeichen, dass abgelesen werden soll.",
      ],
      keyPoints: [
        "Stichworte, Zahlen, Übergangssatz – kein Manuskript.",
        "Richtwert: höchstens rund 120 Wörter Notizen je Folie.",
      ],
      task: {
        instruction:
          "Schreib für mindestens zwei Folien Sprechernotizen mit jeweils mindestens fünf " +
          "Wörtern – und auf keiner Folie mehr als 120 Wörter.",
        hint: "Ansicht → Notizen, dann unter der Folie in den Bereich „Klicken Sie, um Notizen hinzuzufügen“ schreiben.",
        macro: MAKRO_NOTIZEN,
        check: () =>
          pruefeMitMakro(MAKRO_NOTIZEN, (bericht) => {
            const folien = [...bericht.gruppe("folie").entries()];
            const mitNotizen = folien.filter(([, folie]) => Number(folie.get("woerter") ?? 0) >= 5);
            const zuLang = folien.filter(([, folie]) => Number(folie.get("woerter") ?? 0) > 120);
            return fromCriteria(
              [
                criterion(
                  `Mindestens zwei Folien mit Notizen (gefunden: ${mitNotizen.map(([nr]) => nr).join(", ") || "keine"})`,
                  mitNotizen.length >= 2,
                ),
                criterion(
                  zuLang.length === 0
                    ? "Keine Folie hat mehr als 120 Wörter Notizen"
                    : `Zu lange Notizen auf Folie ${zuLang.map(([nr]) => nr).join(", ")}`,
                  zuLang.length === 0,
                ),
              ],
              {
                success: "Die Notizen stehen – schlank genug, um im Vortrag nur hinzuschauen.",
                failure: "Die Notizen passen noch nicht.",
                hint: "Zu lang? Ganze Sätze in Stichworte verwandeln.",
              },
            );
          }),
      },
    },
  ],
};
